import React, { useState } from 'react';
import { IRouteOverview } from '../../models/RouteOverview';
import { Formik, Form } from 'formik';
import { Button, Modal } from 'react-bootstrap';
import { Spinner2 } from '../shared/spinner2';
import TextField from '../shared/formikFields/TextField';
import { debounceEventHandler } from '../shared/submitFormUtility';
import { Form as BsForm, Row, Col } from 'react-bootstrap';
import ToggleButton from '../shared/customInputTypes/ToggleButton';
import RouteService from '../../services/routeService';
import { Toastr } from '../../utils/Toastr';
import { locale } from '../../common/localization/localizationService';
import yup from '../../common/validation';
import { useHistory } from 'react-router-dom';
import routes from '../../common/routes';

interface IRoutesOverviewModalProps {
  customerId: number;
  show: boolean;
  route: IRouteOverview;
  onHide: (rerender: boolean) => void;
  routeIds: Array<string>;
}

export default function RoutesOverviewModal(
  props: IRoutesOverviewModalProps,
): JSX.Element {
  const history = useHistory();

  const getNewRoute = () => {
    const newRoute: IRouteOverview = {
      name: '',
      route: '',
      enabled: true,
      filter: null,
      interval: null,
      lastChanged: null,
    };
    return newRoute;
  };
  const isNew = !props.route;
  const route = props.route ? props.route : getNewRoute();

  const { show, onHide } = props;

  const [isLoading, setLoading] = useState(false);

  const [importRouteStops, setImportRouteStops] = useState(false);

  const [isClearingStops, setIsClearingStops] = useState(false);


  const onSubmit = async (route: IRouteOverview, isNew: boolean) => {
    if (isNew) {
      try {
        setLoading(true);
        await RouteService.createNewRoute(route);
        Toastr.success(locale.routeOverview._routeCreated);
        history.push(routes.importRoutesWizard);
      } catch (error) {
        Toastr.error(error);
      } finally {
        setLoading(false);
        onHide(true);
      }
    } else {
      try {
        setLoading(true);
        await RouteService.updateRoute(route);
        Toastr.success(locale.routeOverview._routeUpdated);
      } catch (error) {
        Toastr.error(error);
      } finally {
        setLoading(false);
        onHide(true);
      }
    }
  };

  const getSchema = () => {
    return yup.object().shape({
      name: yup.string().required().label(locale.routeOverview._routeName),
      route: isNew
        ? yup
            .string()
            .required()
            .test(
              'is-unique',
              locale.routeOverview._routeIdAlreadyExits,
              (routeId: string) =>
                !props.routeIds ||
                !routeId ||
                !props.routeIds.includes(routeId.trim()),
            )
            .label(locale.routeOverview._routeId)
        : yup.string().required().label(locale.routeOverview._routeId),
    });
  };

  const clearAllRouteStops = async () => {
    const routeId = route?.route?.trim?.() ?? '';
    if (!routeId) return;

    const confirmed = window.confirm(
      locale.routeOverview?._confirmDeleteAllRouteStops ??
        'Are you sure you want to delete all route stops? This cannot be undone.',
    );
    if (!confirmed) return;

    try {
      setIsClearingStops(true);
      await RouteService.deleteAllRouteStops(routeId);
      Toastr.success(
        locale.routeOverview?._routeStopsCleared ??
          'All route stops have been deleted.',
      );
      onHide(true);
    } catch (error) {
      Toastr.error(error);
    } finally {
      setIsClearingStops(false);
    }
  };

  return (
    <>
      <Formik
        onSubmit={(route: IRouteOverview) => onSubmit(route, isNew)}
        initialValues={{ ...route, enabled: route.enabled }}
        validationSchema={getSchema}
      >
        {({
          handleSubmit,
          values,
          isSubmitting,
          isValid,
          dirty,
          setFieldValue,
        }) => (
          <Modal
            show={show}
            onHide={() => onHide(false)}
            className={`route-overview-dialog ${
              isNew ? 'new-route-modal' : ''
            }`}
          >
            <Spinner2 show={isLoading} />
            <Modal.Header closeButton>
              <Modal.Title>
                {isNew
                  ? locale.routeOverview._newRoute
                  : locale.routeOverview._routeUpdated}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form noValidate onSubmit={handleSubmit}>
                <TextField
                  label={locale.routeOverview._routeName}
                  name="name"
                  placeholder={locale.routeOverview._routeName}
                />
                <TextField
                  label={locale.routeOverview._routeId}
                  name="route"
                  placeholder={locale.routeOverview._routeId}
                  disabled={!isNew}
                />
                <BsForm.Group
                  as={Row}
                  className="checkbox-row mb-3 route-active-input"
                >
                  <BsForm.Label column sm={6}>
                    {locale.routeOverview._active}
                  </BsForm.Label>
                  <Col sm={6} className="checkbox-wrapper">
                    <ToggleButton
                      onToggle={(e) =>
                        setFieldValue('enabled', e.currentTarget.checked)
                      }
                      value={values.enabled}
                    />
                  </Col>
                  {isNew && (
                    <>
                      <BsForm.Label column sm={6}>
                        {locale.routeOverview._importRouteStops}
                      </BsForm.Label>
                      <Col sm={6} className="checkbox-wrapper">
                        <ToggleButton
                          onToggle={(e) =>
                            setImportRouteStops(e.currentTarget.checked)
                          }
                          value={importRouteStops}
                        />
                      </Col>
                    </>
                  )}
                </BsForm.Group>
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => onHide(false)}>
                {locale.routeOverview._cancel}
              </Button>
              <Button
                type="submit"
                variant="primary"
                onClick={debounceEventHandler(handleSubmit, 250)}
                disabled={
                  !values.name?.trim() || isSubmitting || !isValid || !dirty
                }
              >
                {locale.routeOverview._save}
              </Button>
                {!isNew && (
                <Button
                  variant="danger"
                  onClick={clearAllRouteStops}
                  disabled={isLoading || isClearingStops}
                >
                  {isClearingStops
                    ? (locale.general?._loading)
                    : (locale.routeOverview?._clearAllRouteStops)}
                </Button>
              )}
            </Modal.Footer>
          </Modal>
        )}
      </Formik>
    </>
  );
}
